import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { downloadInvoice } from "Admin/Common/InvoiceList";
import CircularCenteredLoader from "features/common/CircularCenteredLoader";
import DownloadSvg from "images/download.svg";
import { formatDate } from "utils";
import EditPaymentDetailsDialog, {
  modeLabel,
} from "./EditPaymentDetailsDialog";

const useStyles = makeStyles(() => ({
  // Analytics.css ships unscoped table/th/td rules that apply app-wide, so
  // every property this table relies on is pinned here with descendant
  // selectors, which outrank both those globals and MUI's TableCell class.
  tableWrap: {
    // InvoiceList sits in a wrapping column Grid, which sizes its column to
    // the widest child's max-content. width: 0 keeps the 1400px table out of
    // that measurement and minWidth fills the column, so the table scrolls
    // here instead of pushing the page wider and being clipped.
    width: 0,
    minWidth: "100%",
    boxSizing: "border-box",
    boxShadow: "none",
    border: "1px solid #E5E5E5",
    borderRadius: "0",
    overflowX: "auto",
    "& table": {
      width: "100%",
      maxWidth: "none",
      minWidth: "1400px",
      height: "auto",
      margin: 0,
      marginTop: 0,
      borderCollapse: "collapse",
      fontFamily: "inherit",
      fontSize: "inherit",
    },
    "& th, & td": {
      border: "none",
      borderBottom: "1px solid #E5E5E5",
      padding: "10px",
      textAlign: "left",
      verticalAlign: "middle",
    },
    "& thead th": {
      color: "#000",
      fontSize: "15px",
      fontWeight: "500",
      // Wrap headers between words only, never mid-word.
      wordBreak: "normal",
    },
    "& tbody td": {
      color: "black",
      lineHeight: "100%",
      fontSize: "13px",
      whiteSpace: "nowrap",
    },
    "& tbody tr:hover": {
      backgroundColor: "#f7f7f7",
    },
  },
  actions: {
    display: "flex",
    alignItems: "center",
    whiteSpace: "nowrap",
  },
  tableData: {
    color: "black",
    lineHeight: "100%",
    fontSize: "13px",
  },
}));

const COLUMNS = [
  "Name",
  "Invoice date",
  "Date of payment initiation",
  "Case ID",
  "Invoice Number",
  "Payment status",
  "Transaction Reference No",
  "Date of Payment Receipt",
  "Amount received",
  "Mode",
  "",
];

// payment_mode is set on every new invoice, so it doesn't count as a receipt.
const hasPaymentDetails = (invoice) =>
  !!(
    invoice?.transaction_reference_number ||
    invoice?.date_of_payment ||
    (invoice?.amount_recieved != null && invoice?.amount_recieved !== "")
  );

const formatAmount = (amount) =>
  amount != null && amount !== ""
    ? `Rs ${Number(amount).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      })}`
    : "—";

export default function SebiInvoiceTable({
  invoices,
  loading,
  onSaved,
  canEdit = true,
}) {
  const classes = useStyles();
  const [editingInvoice, setEditingInvoice] = useState(null);

  return (
    <>
      <TableContainer component={Paper} className={classes.tableWrap}>
        <Table>
          <TableHead>
            <TableRow>
              {COLUMNS.map((column, index) => (
                <TableCell key={index}>{column}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={COLUMNS.length}>
                  <Box height="4rem">
                    <CircularCenteredLoader />
                  </Box>
                </TableCell>
              </TableRow>
            ) : invoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMNS.length}>No invoices found.</TableCell>
              </TableRow>
            ) : (
              invoices.map((invoice) => {
                const isBankTransfer =
                  invoice?.payment_mode === "bank_transfer";
                return (
                  <TableRow key={invoice.id}>
                    <TableCell>{invoice.full_name_of_user}</TableCell>
                    <TableCell>
                      {invoice.date_and_time
                        ? formatDate(invoice.date_and_time)
                        : null}
                    </TableCell>
                    <TableCell>
                      {isBankTransfer
                        ? "—"
                        : invoice.date_of_payment_initiation
                        ? formatDate(invoice.date_of_payment_initiation)
                        : null}
                    </TableCell>
                    <TableCell>{invoice.disputeId}</TableCell>
                    <TableCell>{invoice.invoice_number}</TableCell>
                    <TableCell>
                      {isBankTransfer ? "Bank transfer" : invoice.payment_status}
                    </TableCell>
                    <TableCell>
                      {invoice.transaction_reference_number || "—"}
                    </TableCell>
                    <TableCell>
                      {invoice.date_of_payment
                        ? formatDate(invoice.date_of_payment)
                        : "—"}
                    </TableCell>
                    <TableCell>{formatAmount(invoice.amount_recieved)}</TableCell>
                    <TableCell>{modeLabel(invoice.payment_mode)}</TableCell>
                    <TableCell>
                      <Box className={classes.actions}>
                        {canEdit && (
                          <Button
                            size="small"
                            variant="outlined"
                            style={{
                              color: "#2F80ED",
                              borderColor: "#2F80ED",
                              textTransform: "none",
                              marginRight: "12px",
                            }}
                            onClick={() => setEditingInvoice(invoice)}
                          >
                            {hasPaymentDetails(invoice) ? "Edit" : "Add"}
                          </Button>
                        )}
                        {invoice.invoice_id && (
                          <IconButton
                            style={{
                              padding: 0,
                            }}
                            onClick={() => downloadInvoice(invoice.invoice_id)}
                          >
                            <Typography className={classes.tableData}>
                              Download
                            </Typography>
                            <img
                              src={DownloadSvg}
                              alt="download invoice"
                              style={{ marginLeft: "8px" }}
                            />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {canEdit && (
        <EditPaymentDetailsDialog
          open={!!editingInvoice}
          invoice={editingInvoice}
          onClose={() => setEditingInvoice(null)}
          onSaved={onSaved}
        />
      )}
    </>
  );
}
